import { useEffect, type RefObject } from 'react';
import { isTopOverlay } from './overlayStack';

/** Tab stops; iframes count so a payment page inside a dialog or overlay stays reachable by keyboard. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

export function focusableIn(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.closest('[aria-hidden="true"]'));
}

/**
 * Keeps Tab focus inside `ref` while `active` and this overlay is on top of the
 * overlay stack; on deactivation focus returns to whatever was focused before.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  overlayId: string,
  initialFocus?: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    const root = ref.current;
    // An overlay that opens under a blocking prompt does not take focus from it.
    if (root && isTopOverlay(overlayId)) {
      const target = initialFocus?.current ?? focusableIn(root)[0] ?? root;
      target.focus({ preventScroll: true });
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !isTopOverlay(overlayId)) return;
      const el = ref.current;
      if (!el) return;
      const items = focusableIn(el);
      if (items.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (!e.shiftKey && (current === last || !el.contains(current))) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && (current === first || !el.contains(current))) {
        e.preventDefault();
        last.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, overlayId]);
}
